export const getFileHTML = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      const fileContent = reader.result;

      resolve(fileContent);
    });

    reader.addEventListener('error', reject);

    reader.readAsText(file);
  });
};

// An XML declaration, when present, is the very first thing in the document —
// only a byte-order mark may precede it. Anchored and `>`-bounded so it can
// never reach a processing instruction further down: an unanchored match would
// also hit `<?xml-stylesheet?>`, and an unbounded one would run from a
// truncated declaration to the next `?>` and swallow the root.
const XML_DECLARATION = /^<\?xml\s[^>]*\?>/i;

// XML's own whitespace, spelled out. `\s` and `trimStart` also match U+00a0,
// which here is the real byte 0xa0 in the middle of a UTF-8 sequence.
const LEADING_WHITESPACE = /^[ \t\r\n]+/;

// A UTF-8 BOM as `atob` hands it over: three latin-1 chars, not one U+feff.
const UTF8_BOM = '\u00EF\u00BB\u00BF';

const XML_DECLARATION_UTF8 = '<?xml version="1.0" encoding="utf-8"?>';

/**
 * customer-api rejects SVG uploads whose XML declaration is missing or carries
 * no `version`, so give them one. The legacy builders each do this in their own
 * vendored wysiwyg helper (see apps/flow-builder/src/vendor/wysiwyg/helpers/
 * imageUpload.js); doing it here covers every builder that uploads through
 * customer-api, including experience-builder.
 *
 * Takes the latin-1 byte string straight out of `atob`, where one char is one
 * byte, and splices only ASCII into it. Decoding to text first would re-encode
 * the body as UTF-8, which mangles an SVG that declares any other encoding —
 * and that is exactly the input class this fixes, since such a declaration is
 * the common way to have one without a `version`. For the same reason an
 * existing declaration keeps the encoding it names: the bytes are unchanged,
 * so renaming their encoding would only make the declaration wrong.
 *
 * @param {string} byteString - latin-1 byte string, one char per byte
 * @return {string} the same bytes, with a versioned declaration at the front,
 *   after the byte-order mark if there is one
 */
export const withXmlDeclaration = byteString => {
  const bom = byteString.startsWith(UTF8_BOM) ? UTF8_BOM : '';
  const body = byteString.slice(bom.length).replace(LEADING_WHITESPACE, '');

  // Not markup at all — a renamed binary, or a gzipped .svgz, both of which
  // Chrome hands over as image/svg+xml.
  if (!body.startsWith('<')) return byteString;

  const [declaration = ''] = body.match(XML_DECLARATION) ?? [];

  // Nothing to add. Any leading whitespace still has to go: a declaration is
  // only a declaration at the top of the document, and one preceded by blank
  // space is a parse error rather than the valid file it looks like.
  if (/\bversion\s*=/i.test(declaration)) return bom + body;

  const versioned = declaration
    ? declaration.replace(/^<\?xml/i, '<?xml version="1.0"')
    : XML_DECLARATION_UTF8;

  return bom + versioned + body.slice(declaration.length);
};

/**
 * Rebuild the uploader's message as the `File` we POST. SVGs get their XML
 * declaration normalized on the way through — this is the one place every
 * builder's upload passes through with the decoded bytes in hand.
 *
 * @param {{ fileData: string, fileName: string, fileType: string }} message
 * @return {File}
 */
export const base64ToFile = ({ fileData, fileName, fileType }) => {
  const base64Data = fileData.includes(',') ? fileData.split(',')[1] : fileData;

  // Convert Base64 to Blob
  const byteCharacters =
    fileType === 'image/svg+xml'
      ? withXmlDeclaration(atob(base64Data))
      : atob(base64Data);

  const byteArrays = new Uint8Array(
    // eslint-disable-next-line unicorn/prefer-code-point
    [...byteCharacters].map(char => char.charCodeAt(0))
  );

  const blob = new Blob([byteArrays], { type: fileType });

  return new File([blob], fileName, { type: fileType });
};
